import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useCustomPrintoutVersion, useCustomPrintoutVersions, useRestoreCustomPrintoutVersion } from "../../../hooks/useCustomPrintoutVersions";

/**
 * Which earlier version of the shown printout is on screen, if any, and that version with its source. Showing another
 * printout (or a new one) forgets it: coming back to this printout shows its current version.
 */
export const useViewedCustomPrintoutVersion = (printoutId: string | null) => {
	const [viewed, setViewed] = useState<{ printoutId: string; versionId: string; versionNumber: number } | null>(null);
	const [ownerId, setOwnerId] = useState<string | null>(printoutId);
	if (ownerId !== printoutId) {
		setOwnerId(printoutId);
		setViewed(null);
	}
	// Also checked here, so the render that switches printouts never shows the old printout's version.
	const current = viewed !== null && viewed.printoutId === printoutId ? viewed : null;
	const viewedVersionId = current === null ? null : current.versionId;
	const versionQuery = useCustomPrintoutVersion(printoutId, viewedVersionId);
	const show = useCallback(
		(versionId: string, versionNumber: number) => {
			if (printoutId === null) throw new Error("an earlier version was asked for with no saved printout shown");
			setViewed({ printoutId, versionId, versionNumber });
		},
		[printoutId],
	);
	const clear = useCallback(() => setViewed(null), []);
	return { viewedVersionId, viewedVersionNumber: current === null ? null : current.versionNumber, versionQuery, show, clear };
};

export type ViewedCustomPrintoutVersion = ReturnType<typeof useViewedCustomPrintoutVersion>;

type Params = {
	printoutId: string | null;
	viewed: ViewedCustomPrintoutVersion;
	/** Runs `onLeave` now, or once the user agrees to lose an unsaved (or running) design. */
	guard: (onLeave: () => void) => void;
	/** Starts the design chat over: anything this session asked for belongs to the version it was asked of. */
	discard: () => void;
};

/**
 * The version history of the shown printout: opening it, viewing an earlier version (through the unsaved-design
 * guard, which also stops a running design), going back to the current one, and restoring — confirmed first, then
 * back to current, which is now the restored copy.
 */
export const useCustomPrintoutVersionActions = ({ printoutId, viewed, guard, discard }: Params) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const [historyWanted, setHistoryWanted] = useState<boolean>(false);
	const [isAskingRestore, setIsAskingRestore] = useState<boolean>(false);
	const versionsQuery = useCustomPrintoutVersions(printoutId, historyWanted || viewed.viewedVersionId !== null);
	const restore = useRestoreCustomPrintoutVersion();
	const versions = versionsQuery.data;
	const eventProps = { department_id: chosenDepartment.department_id, printout_id: printoutId };

	const onHistoryOpenChange = (open: boolean): void => {
		if (!open) return;
		track("custom_printout_version_history_opened", { ...eventProps, viewing_earlier_version: viewed.viewedVersionId !== null });
		setHistoryWanted(true);
		// Colleagues save and restore too: a history already read is read again each time it's opened.
		if (versionsQuery.isFetched) void versionsQuery.refetch();
	};

	// Tracked end to end, so a history that keeps failing reads apart from one a retry fixed.
	const retryHistory = async (): Promise<void> => {
		track("custom_printout_version_history_retry_start", eventProps);
		const result = await versionsQuery.refetch();
		if (result.isError) trackError("custom_printout_version_history_retry_error", { ...eventProps, error_message: result.error.message });
		else if (result.isSuccess) track("custom_printout_version_history_retry_success", { ...eventProps, version_count: result.data.length });
	};

	const view = (versionId: string): void => {
		const target = versions?.find((version) => version.id === versionId);
		if (target === undefined) throw new Error("a version was picked that isn't in the loaded history");
		if (target.isCurrent) {
			viewed.clear();
			return;
		}
		if (versionId === viewed.viewedVersionId) return;
		guard(() => {
			discard();
			viewed.show(versionId, target.versionNumber);
			track("custom_printout_version_viewed", { ...eventProps, version_number: target.versionNumber });
		});
	};

	const confirmRestore = (): void => {
		const { viewedVersionId: versionId, viewedVersionNumber: versionNumber } = viewed;
		if (printoutId === null || versionId === null || versionNumber === null) throw new Error("restore was confirmed with no earlier version on screen");
		restore.mutate(
			{ printoutId, versionId, versionNumber },
			{
				onSuccess: (detail) => {
					viewed.clear();
					showSnackbar(t("printout.custom.ai.version.restore.success", { number: versionNumber, next: detail.versionNumber }), "success");
				},
				onSettled: () => setIsAskingRestore(false),
			},
		);
	};

	return {
		versionsQuery,
		/** The earlier version on screen, or `null` while the current one is. */
		viewedVersionNumber: viewed.viewedVersionNumber,
		/** The viewed version's history row: who saved it, when, and what it restored. */
		viewedSummary: viewed.viewedVersionId === null ? undefined : versions?.find((version) => version.id === viewed.viewedVersionId),
		onHistoryOpenChange,
		retryHistory: () => void retryHistory(),
		view,
		backToCurrent: viewed.clear,
		isAskingRestore,
		askRestore: () => setIsAskingRestore(true),
		cancelRestore: () => setIsAskingRestore(false),
		confirmRestore,
		isRestoring: restore.isPending,
	};
};

export type CustomPrintoutVersionActions = ReturnType<typeof useCustomPrintoutVersionActions>;
